import React from 'react';
import { Link } from 'react-router-dom';
import { QrCode, MessageCircle, Phone, Navigation, Star, Package, Tag, Globe, Check, Printer } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { ServicesPromo } from '../../components/smartqr/ServicesPromo';

const touchpoints = [
  { icon: Package, label: 'Products' }, { icon: Tag, label: 'Offers' }, { icon: MessageCircle, label: 'WhatsApp' },
  { icon: Phone, label: 'Calls' }, { icon: Navigation, label: 'Directions' }, { icon: Star, label: 'Google Reviews' },
];

const freeItems = ['Your own JustOffs business page', 'One permanent QR code', 'Up to 3 products / services', 'WhatsApp, Call, Directions & Google Review buttons', 'Basic QR download', 'Listed on JustOffs'];
const premiumItems = ['Everything in Free', 'Edit business details anytime', 'Add / edit / delete products (up to 50)', 'WhatsApp enquiry button on every product', 'Logo, website & social links', 'Branded printable QR poster', 'Secure management link'];

export const SmartQRLandingPage = () => (
  <div className="min-h-screen bg-slate-50" data-testid="smart-qr-landing">
    <SEO title="JustOffs Smart Business QR - One QR for Your Entire Business" description="Get your business online in 2 minutes. Free JustOffs business page, permanent QR, products, offers, WhatsApp, calls, directions and Google reviews." />
    <Header />

    <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white py-12 md:py-16">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 px-4 py-1.5 rounded-full mb-5 text-orange-300 text-xs font-semibold tracking-wide">
          <QrCode className="w-3.5 h-3.5" /> JUSTOFFS SMART BUSINESS QR
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4">YOUR NEXT CUSTOMER COULD BE <span className="text-orange-400">ONE SCAN AWAY.</span></h1>
        <p className="text-slate-300 text-base md:text-lg mb-2">Get your business online in just 2 minutes.</p>
        <p className="text-slate-400 text-sm mb-8">No website? No problem. We create your business page on JustOffs and list your business for FREE.</p>
        <Link to="/smart-business-qr/create" className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-base transition shadow-lg w-full sm:w-auto" data-testid="create-free-qr-btn">
          <QrCode className="w-5 h-5" /> CREATE MY FREE BUSINESS QR
        </Link>
        <p className="text-slate-500 text-xs mt-3">Free forever. No payment needed to create.</p>
      </div>
    </section>

    <section className="py-10 bg-white">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-lg md:text-xl font-extrabold text-slate-800 mb-2">ONE QR FOR YOUR ENTIRE BUSINESS</h2>
        <p className="text-slate-500 text-sm mb-6">Customers scan once and choose how to reach you.</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {touchpoints.map((t) => (
            <div key={t.label} className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col items-center gap-2">
              <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center"><t.icon className="w-5 h-5 text-white" /></div>
              <span className="text-xs font-semibold text-slate-700">{t.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-8 inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 px-5 py-3 rounded-xl font-bold text-sm">
          <Printer className="w-4 h-4" /> PRINT IT ONCE. SHARE IT EVERYWHERE.
        </div>
        <p className="text-slate-500 text-xs mt-3">Update your products anytime — your QR stays the same.</p>
      </div>
    </section>

    <section className="py-10 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="plan-free">
          <p className="text-xs font-bold text-slate-500 tracking-wide">FREE</p>
          <p className="text-3xl font-extrabold text-slate-800 mb-4">₹0</p>
          <ul className="space-y-2 mb-5">{freeItems.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{i}</li>)}</ul>
          <Link to="/smart-business-qr/create" className="block text-center bg-slate-800 text-white py-3 rounded-xl font-semibold text-sm hover:bg-slate-900 transition" data-testid="plan-free-cta">Create Free QR</Link>
        </div>
        <div className="bg-white rounded-2xl border-2 border-orange-400 p-6 relative" data-testid="plan-premium">
          <span className="absolute -top-3 left-5 bg-orange-500 text-white text-[10px] font-bold px-3 py-1 rounded-full">SMART QR PREMIUM</span>
          <p className="text-xs font-bold text-orange-600 tracking-wide">PREMIUM</p>
          <p className="text-3xl font-extrabold text-slate-800">₹99 <span className="text-sm font-semibold text-slate-500">ONE TIME</span></p>
          <p className="text-xs text-slate-500 mb-4">No subscription. No renewals. Manage & update your page anytime.</p>
          <ul className="space-y-2 mb-5">{premiumItems.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />{i}</li>)}</ul>
          <Link to="/smart-business-qr/create" className="block text-center bg-orange-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-orange-600 transition" data-testid="plan-premium-cta">Start Free, Unlock Later</Link>
        </div>
      </div>
      <p className="text-center text-xs text-slate-500 mt-6 max-w-md mx-auto px-4">Free: create your page + QR + add up to 3 products. ₹99 Premium: manage and update your page anytime.</p>
    </section>

    <section className="py-10 bg-white border-t border-slate-100">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <Globe className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Show your products. Show your offers. Get WhatsApp enquiries.</h2>
        <p className="text-sm text-slate-500 mb-6">Make it easier for customers to call you, find you, and leave a genuine Google Review.</p>
        <Link to="/smart-business-qr/create" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-xl font-bold text-sm transition" data-testid="create-free-qr-btn-bottom">
          <QrCode className="w-5 h-5" /> CREATE MY FREE BUSINESS QR
        </Link>
      </div>
    </section>
    <section className="py-8 bg-slate-50"><div className="max-w-3xl mx-auto px-4"><ServicesPromo /></div></section>
    <Footer />
  </div>
);
